import type {
  GovernedCapabilityAnswerAnchors,
  GovernedCapabilityIntent,
  GovernedCapabilityManifest,
  ScenarioSpec,
} from './governed-capability-manifest';

export type WorkspaceContextScope = {
  scopeKind: string;
  scopeId: string;
  label?: string | null;
};

export type WorkspaceContextFrame = {
  frameId: string;
  packId: string;
  tenantId: string;
  actorId: string;
  contextRevisionId: string;
  capabilityCatalogRevisionId: string;
  activeScope: WorkspaceContextScope;
  packHash: string;
  generatedAt: string;
  expiresAt: string;
};

export type WorkspaceEvidencePosture = {
  domain: string;
  status: 'available' | 'missing' | 'stale' | 'permission_denied' | 'unknown';
  evidenceKeys: string[];
  gaps: string[];
  /**
   * ISO as-of instant per evidence key, when the source system reports one.
   * Optional and additive: a posture without it still admits, the staleness
   * label just reads "as-of date not reported" instead of naming a date.
   * Present so `staleBehavior: 'allow_with_gap'` can produce a dated label at
   * the capability-admission layer rather than admitting stale data silently.
   */
  evidenceAsOf?: Record<string, string>;
  /**
   * Server-only, module-authored identity receipts for admitted evidence. The
   * planner/provider projection intentionally omits these: they bind a
   * validator-approved capability call to the exact artifact it must reload.
   */
  evidenceReceipts?: readonly WorkspaceEvidenceReceipt[];
  /** Loaded source coverage only; not a requested filter or an admission receipt. */
  sourceCoverage?: readonly Readonly<{
    moduleId: string;
    reportingPeriod: string;
    asOf: string;
    sourceClass: string;
    declaredWindow?: Readonly<{ start: string; end: string }>;
  }>[];
  /**
   * #6086 — WHAT THE AMBIGUITY IS BETWEEN, not merely that there is one.
   *
   * A tenant holding more than one ACTIVE plan mapping for a strategy has no
   * lawful single scope to evaluate, so the module refuses — and the reader was
   * told only "could not be completed through the available governed
   * capabilities". Carrying the roots ON the posture lets the blocked answer
   * name them, which is the difference between a generic refusal and a gap the
   * reader can act on. Optional and additive: a posture without it publishes
   * exactly as before.
   */
  activePlanScope?: WorkspaceActivePlanScopeDisclosure;
};

/** #6086 — the closed gap identity a multi-active plan mapping raises. */
export const WORKSPACE_AMBIGUOUS_ACTIVE_PLAN_GAP = 'ambiguous_active_plan';

export type WorkspaceActivePlanScopeDisclosure = Readonly<{
  kind: typeof WORKSPACE_AMBIGUOUS_ACTIVE_PLAN_GAP;
  scopeRootNodeIds: readonly string[];
}>;

export type WorkspaceEvidenceReceipt = Readonly<{
  moduleId: string;
  evidenceKey: string;
  tenantId: string;
  artifactId: string;
  ingestJobId: string;
  contextKind: string;
  schemaVersion: string;
  status: 'APPROVED';
  sourceClass: string;
  sourceRef: string;
  reportingPeriod: string;
  asOf: string;
  calculationMethod: string;
}>;

export type WorkspaceCapabilityCatalogIntent = Pick<
  GovernedCapabilityIntent,
  | 'intentId'
  | 'label'
  | 'description'
  | 'kind'
  | 'examples'
  | 'aliases'
  | 'requiredInputs'
  | 'referenceExports'
  | 'requiredEvidenceDomains'
  | 'requiredEvidenceKeys'
  | 'servesIntentClasses'
  // Producer and comparison identity stay declaration-backed.
  | 'metricProducerRole'
  | 'comparisonBasis'
  // #5841 BAND-AUTHORITY P0 — the capability-owned structured tool-input
  // projection declaration travels onto the catalog so the generic manager
  // builder reads it from the card, never from capability-specific code.
  | 'structuredToolInput'
  | 'contribution'
> & {
  /** Projection-only declaration from the uniquely bound lifecycle tool. */
  requiredPermissions?: readonly string[];
  answerAnchors?: Pick<GovernedCapabilityAnswerAnchors, 'metricKeys'> &
    Partial<
      Pick<GovernedCapabilityAnswerAnchors, 'lexemes' | 'shape' | 'namedGap'>
    >;
  /**
   * #5041 — the intent's declared boundary, mirrored from
   * `executiveAnswerContract.scopeNote`. Descriptive, non-enforcing manifest
   * prose; the planner card shows it so the model can tell a neighbouring
   * intent apart from this one instead of guessing.
   */
  scopeNote?: string;
  /** Operation-selected rollups; registry declarations remain on `metrics[].grain`. */
  metricRequestedGrain?: NonNullable<GovernedCapabilityIntent['metricGrain']>['requestedGrain'];
  /**
   * Baseline defect #2 — the specific `governedTools[]` name THIS intent
   * dispatches through, mirrored from `GovernedCapabilityIntent.toolName`.
   * Absent ⇒ the projector falls back to the capability's own flat
   * `toolName` (the pre-existing, still-correct behaviour for every
   * single-tool capability).
   */
  toolName?: string;
};

export type WorkspaceCapabilityCatalogEntry = Pick<
  GovernedCapabilityManifest,
  | 'capabilityId'
  | 'toolName'
  | 'domain'
  | 'status'
  | 'businessIntents'
  | 'supportedIntentKinds'
  | 'canBeMultiModule'
> & {
  // Optional for hand-built catalog fixtures; manifest projection populates
  // bounded values so planner routing can see authored module prose.
  description?: string;
  executiveQuestions?: string[];
  /** The module's declared numeric output roster. Diagnostic only: anchored keys missing here surface as rosterMismatchKeys; it never removes or grants authority (#6546, #6560). */
  numericBindingsProduced?: readonly string[];
  intents: WorkspaceCapabilityCatalogIntent[];
  // Governed scenario parameter declarations mirrored from the manifest so the
  // deterministic planner path can parse opt-in scenario arguments (for
  // example `reductionPct`) from the ask for a scenario_plan intent. Optional
  // for backward compatibility with directly constructed catalog entries.
  scenarioInputs?: ScenarioSpec[];
};

export type WorkspaceContextEvidenceRef = {
  id: string;
  kind: string;
  label?: string | null;
  claim?: string | null;
  sourceClass?: string | null;
  asOf?: string | null;
};

export type WorkspaceGovernedValueFact = {
  id: string;
  label: string;
  value: number;
  unit: string;
  scope?: string | null;
  timeframe?: string | null;
  evidenceRefId?: string | null;
  definition?: string | null;
};

export type WorkspaceCompanySegmentContext = {
  id: string;
  label: string;
  description?: string | null;
  evidenceRefIds?: string[];
};

export type WorkspaceCompanyNarrativeSectionContext = {
  id: string;
  label: string;
  body: string;
  evidenceRefIds?: string[];
};

export type WorkspaceCompanyEconomicModelContext = {
  primaryEconomicPool?: string | null;
  revenueCaveat?: string | null;
  oiBridgeLanguage?: string | null;
  sourceRefIds?: string[];
};

export type WorkspaceCompanyOperatingSignalContext = {
  id: string;
  label: string;
  description: string;
  evidenceRefIds?: string[];
};

export type WorkspaceCompanyTerminologyAliasContext = {
  aliases: string[];
  canonical: string;
};

export type WorkspaceCompanyCompositionBoundaryContext = {
  includedHere?: string[];
  excludedBecauseSuppliedByOtherGovernedLayers?: string[];
};

export type WorkspaceCompanyExecutiveDecisionFrameContext = {
  id: string;
  label: string;
  purpose?: string | null;
  evidenceRefIds?: string[];
};

export type WorkspaceCompanyProfileContext = {
  status: 'available' | 'missing' | 'stale' | 'permission_denied';
  name?: string | null;
  industry?: string | null;
  businessModel?: string | null;
  richNarrative?: WorkspaceCompanyNarrativeSectionContext[];
  economicModel?: WorkspaceCompanyEconomicModelContext | null;
  segments?: WorkspaceCompanySegmentContext[];
  scaleFacts?: WorkspaceGovernedValueFact[];
  operatingSignals?: WorkspaceCompanyOperatingSignalContext[];
  operatingTerms?: string[];
  decisionTerms?: string[];
  voiceRules?: string[];
  evidenceBoundaries?: string[];
  terminologyAliases?: WorkspaceCompanyTerminologyAliasContext[];
  compositionBoundary?: WorkspaceCompanyCompositionBoundaryContext | null;
  executiveDecisionFrames?: WorkspaceCompanyExecutiveDecisionFrameContext[];
  profileId?: string | null;
  profileVersion?: string | null;
  evidenceRefs?: WorkspaceContextEvidenceRef[];
  gaps?: string[];
};

export type WorkspaceOrgRoleContext = {
  title: string;
  level?: string | null;
  functionName?: string | null;
};

export type WorkspaceOrgCostLeverEvidenceContext = {
  status: 'available' | 'missing' | 'stale' | 'permission_denied';
  evidenceKey: string;
  evidencePackArtifactId?: string | null;
  approvedAt?: string | null;
  sourceRefCount?: number;
  gaps?: string[];
};

export type WorkspaceOrganizationContext = {
  status: 'available' | 'missing' | 'stale' | 'permission_denied';
  orgContextId?: string | null;
  asIsOrgStateId?: string | null;
  name?: string | null;
  positionCount?: number | null;
  positionCostRowCount?: number | null;
  sampledRoles?: WorkspaceOrgRoleContext[];
  orgCostLeverEvidence?: WorkspaceOrgCostLeverEvidenceContext | null;
  evidenceRefs?: WorkspaceContextEvidenceRef[];
  gaps?: string[];
};

export type WorkspaceStrategyContextItem = {
  id: string;
  label: string;
  description?: string | null;
  category?: string | null;
  priority?: string | null;
  criticality?: string | number | null;
  sourceKind?: 'current_observation' | 'working_draft';
  evidenceRefIds?: string[];
};

/** Metadata proves a referenced draft document exists; it is never its body text. */
export type WorkspaceCurrentDraftDocumentRef = {
  id: string;
  filename: string;
  mimeType: string;
  bytes: number;
  pages?: number | null;
  status: string;
  createdAt: string;
  contentAvailability: 'metadata_only';
};

export type WorkspaceStrategicObjectiveBindingContext = {
  id: string;
  objectiveType: string;
  label: string;
  metricKey: string;
  unit: string;
  direction: string;
  baselineValue: number;
  targetValue: number;
  timeframeStart: string;
  timeframeEnd: string;
  ownerUserId?: string | null;
  ownerLabel?: string | null;
  reviewCadence?: string | null;
  approvedAt?: string | null;
  evidenceRefIds?: string[];
};

/** Retention provenance is neither approval nor summary/projection coverage. */
export type WorkspaceStrategySourceProvenance =
  | { status: 'invalid'; packVersionId: string; reason: 'snapshot_shape_invalid' | 'snapshot_scope_mismatch' | 'snapshot_digest_mismatch' }
  | {
      status: 'available';
      interpretation: 'retained_sources_not_approval_or_summary_coverage';
      packVersionId: string;
      inputsHash: string;
      summaryInputsHash: string;
      sourceSchemaVersion: string;
      sourceDigest: string;
      scope: { kind: 'tenant_strategy_sources'; requestedStrategyId: string };
      retainedRecordCounts: { strategies: number; constraints: number; principles: number; capabilities: number };
      unresolvedFindingCount: number;
      unresolvedFindingBasis: 'recorded_not_digest_bound';
      unresolvedFindings: Array<{
        kind: 'principle' | 'capability'; id: string;
        reason: 'source_tenant_mismatch' | 'source_strategy_unresolved';
      }>;
    };

export type WorkspaceStrategyPackContext = {
  status: 'available' | 'missing' | 'stale' | 'permission_denied';
  strategyId?: string | null;
  packId?: string | null;
  packVersionId?: string | null;
  sourceProvenance?: WorkspaceStrategySourceProvenance;
  asOf?: string | null;
  summary?: string | null;
  pillars?: WorkspaceStrategyContextItem[];
  strategies?: WorkspaceStrategyContextItem[];
  designPrinciples?: WorkspaceStrategyContextItem[];
  guardrails?: WorkspaceStrategyContextItem[];
  capabilities?: WorkspaceStrategyContextItem[];
  currentDraftDocumentRefs?: WorkspaceCurrentDraftDocumentRef[];
  objectiveBindings?: WorkspaceStrategicObjectiveBindingContext[];
  governedValues?: WorkspaceGovernedValueFact[];
  evidenceRefs?: WorkspaceContextEvidenceRef[];
  gaps?: string[];
};

export type WorkspaceNeuroTruthPackContext = {
  status: 'available' | 'missing' | 'stale' | 'permission_denied';
  packId?: string | null;
  packVersionId?: string | null;
  asOf?: string | null;
  summary?: {
    mission?: string | null;
    priorities?: string[];
    constraints?: string[];
  };
  rules?: WorkspaceStrategyContextItem[];
  patterns?: WorkspaceStrategyContextItem[];
  capabilities?: WorkspaceStrategyContextItem[];
  evidenceRefs?: WorkspaceContextEvidenceRef[];
  gaps?: string[];
};

export type WorkspaceRichContextStatus = {
  expected: boolean;
  status: 'available' | 'missing' | 'stale' | 'permission_denied';
  missingKeys: string[];
  gaps: string[];
};

/** A server-selected approved source record; authorization stays upstream. */
export type WorkspaceApprovedSourceArtifact = Readonly<{
  id: string;
  title: string;
  description: string | null;
  contentJson: unknown;
}>;

export type WorkspaceContextPack = WorkspaceContextFrame & {
  company: {
    status: 'available' | 'permission_denied';
    companyId: string | null;
    label: string | null;
    industry?: string | null;
    profile?: WorkspaceCompanyProfileContext | null;
  };
  organization?: WorkspaceOrganizationContext | null;
  strategy: {
    status: 'available' | 'missing' | 'stale' | 'permission_denied';
    summary?: string | null;
    strategyId?: string | null;
    packVersionId?: string | null;
    strategyStateVersion?: number | null;
    objectives?: string[];
    constraints?: Record<string, number | null>;
    requiredCapabilityIds?: string[];
    projectionStatus?: 'fresh' | 'missing' | 'stale';
    objectiveRefs?: string[];
    constraintRefs?: string[];
    capabilityRefs?: string[];
    relationshipContext?: import('./strategy-relationship-context').StrategyRelationshipContext;
    gaps?: string[];
    strategyPack?: WorkspaceStrategyPackContext | null;
    neuroTruth?: WorkspaceNeuroTruthPackContext | null;
  };
  richContext?: WorkspaceRichContextStatus;
  /** Present only when the controller supplied already-authorized source data. */
  approvedSourceArtifacts?: readonly WorkspaceApprovedSourceArtifact[];
  evidencePosture: WorkspaceEvidencePosture[];
  capabilityCatalog: WorkspaceCapabilityCatalogEntry[];
};
